<script setup lang="ts">

import CodeMirror from 'vue-codemirror6';
import { EditorView } from '@codemirror/view';
import type { Text } from '@codemirror/state';

defineProps<{
    modelValue: string;
    label: string;
    id?: string;
    invalid?: boolean;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    'update:modelValue': [value: string]
}>();

const { jsonataLanguage } = useJsonataEditor();

const isFocused = ref(false);
const codeMirrorRef = ref<InstanceType<typeof CodeMirror> | null>(null);

const theme = EditorView.theme({
    '&': {
        backgroundColor: 'transparent',
        borderRadius: '6px'
    },
    '&.cm-focused': {
        outline: 'none'
    },
    '.cm-content': {
        caretColor: 'var(--text-color)',
        backgroundColor: 'transparent',
        fontFamily: 'var(--font-family)',
        fontSize: '1rem',
        padding: '0',
        tabIndex: '0'
    },
    '.cm-gutters': { 
        display: 'none' 
    },
    '.cm-scroller': {
        fontFamily: 'inherit',
        backgroundColor: 'transparent'
    },
    '.cm-line': {
        padding: '0'
    },
    '.cm-activeLine, .cm-activeLineGutter': { 
        backgroundColor: 'transparent' 
    },
    '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
        backgroundColor: 'var(--primary-color-lighter, rgba(59, 130, 246, 0.25)) !important'
    }
});

const focusExtension = EditorView.domEventHandlers({
    focus: () => {
        isFocused.value = true;
        return false;
    },
    blur: () => {
        isFocused.value = false;
        return false;
    }
});

const extensions = computed(() => [
    EditorView.lineWrapping,
    theme,
    focusExtension
]);

onMounted(() => {
    nextTick(() => {
        const content = codeMirrorRef.value?.$el?.querySelector('.cm-content') as HTMLElement;
        if (content) {
            content.setAttribute('tabindex', '0');
        }
    });
});

function handleUpdate (value: string | Text | undefined) {
    emit('update:modelValue', value === undefined ? '' : value.toString());
}

</script>

<template>
    <LabelledField :label="label" :label-for="id">
        <div
            class="p-inputwrapper w-full"
            :class="{ 
                'p-inputwrapper-invalid': invalid
            }"
        >
            <code-mirror
                :id="id"
                ref="codeMirrorRef"
                :model-value="modelValue"
                :lang="jsonataLanguage"
                :extensions="extensions"
                :disabled="disabled"
                class="p-inputtext p-component w-full codemirror-wrapper"
                :class="{ 'p-invalid': invalid }"
                @update:model-value="handleUpdate($event)"
            />
        </div>
    </LabelledField>
</template>

<style scoped>
.p-inputwrapper {
    position: relative;
}

.codemirror-wrapper {
    background: var(--surface-ground);
    border-radius: 0.375rem;
    border: 1px solid var(--surface-border, #cbd5e1);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.codemirror-wrapper :deep(.cm-editor) {
    height: 100%;
}

.p-inputwrapper-invalid .codemirror-wrapper {
    border-color: var(--red-500, #f87171);
}
</style>
